// File: Admin.js
//
// The staff/admin portal. This is the biggest page in the app and is
// gated to users with `staff = true` in their Firestore profile.
// Some features (creating new admins, approving/disapproving users,
// editing inquiry types) are further gated to `management = true`.
//
// The page is organized into three tabs at the top:
//   1. INQUIRIES   - Triage incoming user inquiries: view, claim,
//                    add notes, change status, etc.
//   2. STAFF       - See who's currently online/offline, see/edit
//                    their info, give people admin rights (mgmt only).
//   3. MANAGEMENT  - Edit the list of inquiry types users can pick
//                    from (and configure auto-thread settings).
//                    Only visible to users with management = true.
//
// What's likely to be changed in this file:
//   - To add a column to the inquiries list -> search for "InquiryMetaRow"
//   - To change the default inquiry status badges -> see StatusPill
//   - To adjust who can do what -> look for `canManage` checks
//
// Sections in this file:
//   - Status/urgency/presence pill components
//   - Icons
//   - Layout helpers (CardShell, CardHeader, SoftPanel, etc.)
//   - Button helpers (GreenButton, SecondaryButton, DangerButton)
//   - Date/format helpers (toDateSafe, formatShortDate, urgencyRank)
//   - Admin (the actual page: state, data loading, the three tabs)

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "./assets/512.png";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "./firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  limit,
  where,
  getDocs,
  setDoc,
  getDocs as fetchDocs,
} from "firebase/firestore";

import { DEFAULT_INQUIRY_TYPES, slugifyInquiryTypeKey } from "./inquiryTypeCatalog";
import {
  COLORS,
  THEME,
  pageBackground,
  DEFAULT_COLORS,
  COLOR_KEYS,
  DEFAULT_ROLES,
  ROLE_KEYS,
  applyColors,
  applyRoles,
} from "./theme";

const db = getFirestore();
const functions = getFunctions();

// --- Pills (small colored labels) ---
// A plain green pill. Used as a generic label.
function Pill({ children }) {
  return (
    <span
      className="text-xs font-extrabold px-2.5 py-1 rounded-full border"
      style={{
        backgroundColor: COLORS.greenLight,
        borderColor: THEME.border,
        color: THEME.text,
      }}
    >
      {children}
    </span>
  );
}

// Inquiry status badge. Colors change based on the status string
// ("new" / "in_progress" / "resolved" / etc.). Add new statuses by
// adding branches inside this function.
function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const base = "text-xs font-extrabold px-2.5 py-1 rounded-full border";

  if (s === "new") {
    return (
      <span
        className={base}
        style={{
          backgroundColor: COLORS.yellowBase,
          borderColor: "rgba(61,51,0,0.2)",
          color: COLORS.brown,
        }}
      >
        New
      </span>
    );
  }

  if (s === "resolved" || s === "closed") {
    return (
      <span
        className={base}
        style={{
          backgroundColor: COLORS.greenLight,
          borderColor: THEME.border,
          color: COLORS.greenDark,
        }}
      >
        Closed
      </span>
    );
  }

  return (
    <span
      className={base}
      style={{
        backgroundColor: COLORS.aquaLight,
        borderColor: "rgba(0,96,110,0.18)",
        color: COLORS.aquaDeep,
      }}
    >
      Open
    </span>
  );
}

// Urgency badge (low / medium / high). Color-coded so an admin can
// scan the inquiry list and see what's urgent at a glance.
function UrgencyPill({ urgency }) {
  const u = (urgency || "").toLowerCase();
  const base = "text-xs font-extrabold px-2.5 py-1 rounded-full border";

  if (u === "high") {
    return <span className={`${base} bg-red-600 text-white border-red-700/20`}>Urgent</span>;
  }

  if (u === "medium") {
    return (
      <span
        className={base}
        style={{
          backgroundColor: COLORS.yellowLight,
          borderColor: "rgba(61,51,0,0.2)",
          color: COLORS.brown,
        }}
      >
        Medium
      </span>
    );
  }

  return (
    <span
      className={base}
      style={{
        backgroundColor: COLORS.greenLight,
        borderColor: THEME.border,
        color: COLORS.greenDark,
      }}
    >
      Low
    </span>
  );
}

// "Online / Offline" badge for staff members on the Staff tab.
// Shows green when online, gray when offline.
function PresencePill({ status }) {
  const s = (status || "offline").toLowerCase();
  const base = "text-xs font-extrabold px-2.5 py-1 rounded-full border";

  if (s === "online") {
    return (
      <span
        className={base}
        style={{
          backgroundColor: COLORS.greenBase,
          borderColor: "rgba(64,115,0,0.18)",
          color: "#ffffff",
        }}
      >
        Online
      </span>
    );
  }

  if (s === "away") {
    return (
      <span
        className={base}
        style={{
          backgroundColor: COLORS.yellowBase,
          borderColor: "rgba(61,51,0,0.2)",
          color: COLORS.brown,
        }}
      >
        Away
      </span>
    );
  }

  return (
    <span
      className={base}
      style={{
        backgroundColor: "rgba(255,255,255,0.75)",
        borderColor: THEME.border,
        color: "rgba(31,56,0,0.58)",
      }}
    >
      Offline
    </span>
  );
}

function IconInquiry({ className = "w-6 h-6" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 10h8M8 14h5" />
      <path d="M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z" />
    </svg>
  );
}

function IconMessages({ className = "w-6 h-6" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}

function IconUsers({ className = "w-6 h-6" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6" />
      <path d="M23 11h-6" />
    </svg>
  );
}

function IconShield({ className = "w-6 h-6" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
      <path d="M9.5 12.5l1.8 1.8 3.7-4.3" />
    </svg>
  );
}

function IconList({ className = "w-6 h-6" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  );
}

// One row in the inquiry list (left side of the Inquiries tab).
// Clicking it selects that inquiry and opens it on the right.
// `active` styles the currently-selected row.
function ListRow({ active, onClick, title, subtitle, meta, right }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={[
        "w-full text-left rounded-2xl border transition-all duration-200 ease-out",
        "active:scale-[0.99] px-4 py-3",
        active ? "shadow-sm" : "hover:-translate-y-0.5",
      ].join(" ")}
      style={{
        backgroundColor: active ? COLORS.greenLight : THEME.cardBg,
        borderColor: active ? "rgba(64,115,0,0.24)" : THEME.border,
        boxShadow: active ? "0 10px 22px rgba(31,56,0,0.1)" : "0 8px 20px rgba(31,56,0,0.06)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-extrabold truncate text-sm" style={{ color: THEME.text }}>
            {title}
          </div>
          <div className="text-xs mt-1 whitespace-pre-line" style={{ color: "rgba(31,56,0,0.62)" }}>
            {subtitle}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {right}
          {meta ? <div className="text-xs" style={{ color: COLORS.aquaDark }}>{meta}</div> : null}
        </div>
      </div>
    </button>
  );
}

// Light placeholder shown when a list is empty (e.g. "No inquiries").
function EmptyBox({ label }) {
  return (
    <div
      className="rounded-2xl border backdrop-blur p-6 text-sm text-center"
      style={{
        backgroundColor: THEME.cardBgSoft,
        borderColor: THEME.border,
        color: "rgba(31,56,0,0.62)",
      }}
    >
      {label}
    </div>
  );
}

// --- Buttons used throughout the admin page ---
// The primary action button (filled green).
function GreenButton({ children, onClick, className = "", title, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center gap-2",
        "rounded-xl border px-3 py-2.5",
        "text-sm font-extrabold text-white shadow-sm transition",
        disabled ? "opacity-55 cursor-not-allowed" : "hover:-translate-y-0.5 hover:opacity-95 active:scale-[0.99]",
        className,
      ].join(" ")}
      style={{
        backgroundColor: THEME.primary,
        borderColor: "rgba(64,115,0,0.2)",
        boxShadow: disabled ? "none" : "0 10px 20px rgba(64,115,0,0.16)",
      }}
    >
      {children}
    </button>
  );
}

// A less prominent button (white outlined). Used for "Cancel" /
// "Refresh" / other non-primary actions.
function SecondaryButton({ children, onClick, className = "", disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center gap-2",
        "rounded-xl border px-3 py-2.5",
        "text-sm font-extrabold bg-white transition",
        disabled ? "opacity-55 cursor-not-allowed" : "hover:-translate-y-0.5 hover:bg-[#E7F7D2] active:scale-[0.99]",
        className,
      ].join(" ")}
      style={{
        borderColor: THEME.border,
        color: THEME.text,
        boxShadow: disabled ? "none" : "0 8px 18px rgba(31,56,0,0.08)",
      }}
    >
      {children}
    </button>
  );
}

// A red button for destructive actions (delete, revoke, etc.).
function DangerButton({ children, onClick, className = "", disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center gap-2",
        "rounded-xl border border-red-700/20 px-3 py-2.5",
        "text-sm font-extrabold text-white shadow-sm transition",
        disabled ? "bg-red-400 opacity-60 cursor-not-allowed" : "bg-red-700 hover:bg-red-800",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// --- Layout helpers ---
// Generic rounded card wrapper used a LOT on this page. Children
// passed in get the standard background, border, and shadow.
function CardShell({ children, className = "" }) {
  return (
    <div
      className={`backdrop-blur border rounded-3xl shadow-md ${className}`}
      style={{
        backgroundColor: THEME.cardBg,
        borderColor: THEME.border,
        boxShadow: THEME.shadow,
      }}
    >
      {children}
    </div>
  );
}

// A card header (icon + title on the left, optional right-side
// content). Sits inside a CardShell.
function CardHeader({ title, rightSlot, icon }) {
  return (
    <div className="px-6 py-5 border-b shrink-0" style={{ borderColor: THEME.border }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3">
          {icon ? (
            <span
              className="rounded-xl p-2 border bg-white"
              style={{
                color: THEME.primary,
                borderColor: THEME.border,
              }}
            >
              {icon}
            </span>
          ) : null}
          <div className="text-base sm:text-lg font-extrabold truncate" style={{ color: THEME.text }}>
            {title}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">{rightSlot}</div>
      </div>
    </div>
  );
}

function SoftPanel({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl p-2.5 min-h-0 border ${className}`}
      style={{
        backgroundColor: THEME.surfaceTint,
        borderColor: "rgba(31,56,0,0.08)",
      }}
    >
      {children}
    </div>
  );
}

function SectionCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border backdrop-blur p-5 shadow-sm ${className}`}
      style={{
        backgroundColor: THEME.cardBgSoft,
        borderColor: THEME.border,
        boxShadow: "0 12px 24px rgba(31,56,0,0.08)",
      }}
    >
      {children}
    </div>
  );
}

// One of the top tab buttons (Inquiries / Staff Status / Management).
// `active` styles the currently-selected tab green.
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-2xl px-4 py-2.5 text-sm font-extrabold border transition",
        active ? "text-white shadow-sm" : "bg-white/80 hover:bg-[#E7F7D2]",
      ].join(" ")}
      style={{
        backgroundColor: active ? THEME.headerBg : undefined,
        borderColor: active ? "rgba(231,247,210,0.28)" : THEME.border,
        color: active ? "#ffffff" : THEME.text,
        boxShadow: active ? "0 10px 20px rgba(31,56,0,0.14)" : "0 8px 18px rgba(31,56,0,0.06)",
      }}
    >
      {children}
    </button>
  );
}

// The line of pills (status + urgency + claimed-by) shown beneath
// the inquiry title in the list. Edit here to change what info shows
// up on each inquiry row.
function InquiryMetaRow({ inquiry, showClaimed = false }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {showClaimed && inquiry.claimedByName ? <Pill>{inquiry.claimedByName}</Pill> : null}
      {!showClaimed ? <Pill>Unclaimed</Pill> : null}
      <UrgencyPill urgency={inquiry.urgency} />
      <StatusPill status={inquiry.status} />
    </div>
  );
}

// --- Date / sorting helpers ---
// Best-effort conversion of "some date-ish value" (Firestore Timestamp,
// JS Date, or null) into a JS Date object. Returns null if it can't.
function toDateSafe(ts) {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(ts) {
  const d = toDateSafe(ts);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateTime(ts) {
  const d = toDateSafe(ts);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Maps a string urgency ("high"/"medium"/"low") to a number we can
// sort by. Higher number = more urgent. Used to put the most urgent
// inquiries at the top of the list.
function urgencyRank(u) {
  const x = (u || "").toLowerCase();
  if (x === "high") return 3;
  if (x === "medium") return 2;
  return 1;
}

function isClosedStatus(status) {
  const s = (status || "").toLowerCase();
  return s === "resolved" || s === "closed";
}

function getUserDisplayName(user) {
  const full = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  return full || user?.name || user?.displayName || user?.email || user?.uid || "Unknown";
}

function getEffectiveStaffStatus(user) {
  const raw = (user?.staffStatus || "offline").toLowerCase();
  const lastActive = toDateSafe(user?.lastActiveAt);
  if (!lastActive) return "offline";

  const diff = Date.now() - lastActive.getTime();
  const onlineWindow = 5 * 60 * 1000;

  if (diff > onlineWindow) return "offline";
  if (raw === "away") return "away";
  return "online";
}

function isStaffLike(user) {
  return user?.staff === true || user?.management === true;
}

function normalizeInquiryTypeForEditor(item = {}) {
  return {
    id: item.id || "",
    key: item.key || "",
    label: item.label || "",
    description: item.description || "",
    sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 9999,
    active: item.active !== false,
    archived: item.archived === true,
    autoOpenThread: item.autoOpenThread === true,
    defaultThreadMessage: item.defaultThreadMessage || "",
    followUpQuestions: Array.isArray(item.followUpQuestions) ? item.followUpQuestions : [],
  };
}

function buildInquiryTypeList(list) {
  return [...list].sort((a, b) => {
    const orderA = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : 9999;
    const orderB = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : 9999;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

function ManagementSubTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-xl px-3 py-2 text-sm font-extrabold border transition",
        active ? "shadow-sm" : "bg-transparent hover:bg-white/70",
      ].join(" ")}
      style={{
        backgroundColor: active ? "#ffffff" : "transparent",
        borderColor: active ? THEME.border : "transparent",
        color: active ? THEME.text : "rgba(31,56,0,0.62)",
      }}
    >
      {children}
    </button>
  );
}

// Small paint-palette icon used as the Theme sub-tab card icon.
function IconPalette({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22a10 10 0 110-20 10 10 0 016 18c-1.5 0-2-1-2-2s.5-2 0-3-2-1-3 0c-.7.7-1 2-1 3a4 4 0 01-4 4z" />
      <circle cx="7.5" cy="10.5" r="1" />
      <circle cx="12" cy="7.5" r="1" />
      <circle cx="16.5" cy="10.5" r="1" />
    </svg>
  );
}

// ================================================================
// Color editor (Management → Theme sub-tab)
//
// Renders a row per color in the brand palette. Each row has a native
// color picker + a hex text input. Editing a color previews the change
// instantly (applyColors writes the new value to a CSS variable on the
// document root, and every component using that variable repaints).
// The Save button writes the full palette to Firestore at
// config/theme; the app's top-level useEffect (in App.js) is
// subscribed to that doc and pushes the changes to every user's
// browser without a page reload.
// ================================================================
// Pull the family name + variant ("Base"/"Light"/"Dark"/"Deep") out of
// a color key. Standalone keys (e.g. "brown") get variant = null and
// the whole key as the family name.
function colorFamilyInfo(key) {
  for (const suffix of ["Light", "Dark", "Deep"]) {
    if (key.endsWith(suffix) && key.length > suffix.length) {
      return { family: key.slice(0, -suffix.length), variant: suffix };
    }
  }
  if (key.endsWith("Base") && key.length > 4) {
    return { family: key.slice(0, -4), variant: "Base" };
  }
  return { family: key, variant: null };
}

// Group a flat list of color keys into families. Each family has one
// "base" (the canonical color) plus zero-or-more variants. Insertion
// order is preserved so the on-screen ordering follows DEFAULT_COLORS.
function groupColorKeys(keys) {
  const groups = new Map();
  keys.forEach((key) => {
    const { family, variant } = colorFamilyInfo(key);
    if (!groups.has(family)) {
      groups.set(family, { name: family, base: null, variants: [] });
    }
    const g = groups.get(family);
    if (variant === "Base" || variant === null) {
      g.base = key;
    } else {
      g.variants.push({ key, variant });
    }
  });
  // If a family has only variants (no base yet — e.g. only added
  // "tealLight"), promote the first variant into the base slot so the
  // family card still has something to display.
  for (const g of groups.values()) {
    if (!g.base && g.variants.length > 0) {
      g.base = g.variants[0].key;
      g.variants.shift();
    }
  }
  const variantOrder = { Light: 1, Dark: 2, Deep: 3 };
  for (const g of groups.values()) {
    g.variants.sort(
      (a, b) => (variantOrder[a.variant] || 99) - (variantOrder[b.variant] || 99)
    );
  }
  return Array.from(groups.values());
}

// One row used inside an expanded family card (Light / Dark / Deep
// variant). Compact version of the base-color row above.
function ColorRow({
  colorKey,
  value,
  defaultValue,
  onChange,
  onReset,
  onDelete,
}) {
  const safeValue = value || "#000000";
  const isDefault =
    defaultValue && safeValue.toLowerCase() === defaultValue.toLowerCase();
  const isCustom = !defaultValue;
  return (
    <div
      className="flex items-center gap-2 rounded-lg border p-2"
      style={{
        borderColor: THEME.border,
        backgroundColor: "rgba(255,255,255,0.55)",
      }}
    >
      <input
        type="color"
        value={safeValue}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`${colorKey} color picker`}
        className="w-9 h-9 rounded cursor-pointer border shrink-0"
        style={{ borderColor: THEME.border, padding: 0 }}
      />
      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-extrabold truncate"
          style={{ color: THEME.text }}
        >
          {colorKey}
        </div>
        <input
          type="text"
          value={safeValue}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className="mt-0.5 w-full rounded border px-2 py-0.5 text-[11px] font-mono"
          style={{ borderColor: THEME.border }}
        />
      </div>
      {isCustom ? (
        <button
          type="button"
          onClick={onDelete}
          className="text-[10px] font-extrabold rounded px-2 py-1 border transition hover:bg-white"
          style={{
            borderColor: THEME.border,
            color: "#a00",
            backgroundColor: "transparent",
          }}
          title="Delete this custom color"
        >
          Delete
        </button>
      ) : (
        <button
          type="button"
          onClick={onReset}
          disabled={isDefault}
          className={[
            "text-[10px] font-extrabold rounded px-2 py-1 border transition",
            isDefault ? "opacity-40 cursor-not-allowed" : "hover:bg-white",
          ].join(" ")}
          style={{
            borderColor: THEME.border,
            color: THEME.text,
            backgroundColor: "transparent",
          }}
          title={isDefault ? "Already at default" : `Reset to ${defaultValue}`}
        >
          Reset
        </button>
      )}
    </div>
  );
}

// The collapsible card for one color family. Shows the family's base
// color prominently and a "N variants" toggle. When expanded, the
// Light / Dark / Deep rows appear below.
function ColorFamilyCard({
  group,
  colors,
  defaults,
  isExpanded,
  onToggle,
  onChange,
  onReset,
  onDelete,
}) {
  const baseKey = group.base;
  const baseValue = colors[baseKey] || "#000000";
  const baseDefault = defaults[baseKey];
  const isDefaultBase =
    baseDefault && baseValue.toLowerCase() === baseDefault.toLowerCase();
  const isCustom = !(baseKey in defaults);
  const hasVariants = group.variants.length > 0;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        borderColor: THEME.border,
        backgroundColor: "rgba(255,255,255,0.6)",
      }}
    >
      <div className="flex items-center gap-3 p-3">
        <input
          type="color"
          value={baseValue}
          onChange={(e) => onChange(baseKey, e.target.value)}
          aria-label={`${group.name} base color`}
          className="w-12 h-12 rounded-lg cursor-pointer border shrink-0"
          style={{ borderColor: THEME.border, padding: 0 }}
        />
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-extrabold capitalize truncate"
            style={{ color: THEME.text }}
          >
            {group.name}
          </div>
          <div
            className="text-[10px] font-mono truncate"
            style={{ color: "rgba(31,56,0,0.55)" }}
          >
            {baseKey}
          </div>
          <input
            type="text"
            value={baseValue}
            onChange={(e) => onChange(baseKey, e.target.value)}
            spellCheck={false}
            className="mt-1 w-full rounded border px-2 py-1 text-xs font-mono"
            style={{ borderColor: THEME.border }}
          />
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {hasVariants ? (
            <button
              type="button"
              onClick={onToggle}
              className="text-xs font-extrabold rounded-lg px-2 py-1 border transition hover:bg-white"
              style={{
                borderColor: THEME.border,
                color: THEME.text,
                backgroundColor: "transparent",
              }}
              title={isExpanded ? "Hide variants" : "Show variants"}
            >
              {isExpanded
                ? "Hide"
                : `${group.variants.length} variant${
                    group.variants.length === 1 ? "" : "s"
                  }`}
            </button>
          ) : null}
          {isCustom ? (
            <button
              type="button"
              onClick={() => onDelete(baseKey)}
              className="text-[10px] font-extrabold rounded px-2 py-1 border transition hover:bg-white"
              style={{
                borderColor: THEME.border,
                color: "#a00",
                backgroundColor: "transparent",
              }}
              title="Delete this custom color"
            >
              Delete
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onReset(baseKey)}
              disabled={isDefaultBase}
              className={[
                "text-[10px] font-extrabold rounded px-2 py-1 border transition",
                isDefaultBase ? "opacity-40 cursor-not-allowed" : "hover:bg-white",
              ].join(" ")}
              style={{
                borderColor: THEME.border,
                color: THEME.text,
                backgroundColor: "transparent",
              }}
              title={
                isDefaultBase
                  ? "Already at default"
                  : `Reset to ${baseDefault}`
              }
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {hasVariants && isExpanded ? (
        <div
          className="border-t p-3 space-y-2"
          style={{ borderColor: THEME.border }}
        >
          {group.variants.map(({ key }) => (
            <ColorRow
              key={key}
              colorKey={key}
              value={colors[key]}
              defaultValue={defaults[key]}
              onChange={(v) => onChange(key, v)}
              onReset={() => onReset(key)}
              onDelete={() => onDelete(key)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Inline form to add a custom color. Validates the name (JS-identifier
// shape, not already taken) and the hex value. Custom colors get
// stored as CSS variables — to actually USE one in the site, reference
// it via `var(--color-NAME)` from JS/CSS.
function AddColorForm({ onAdd, onCancel, existingKeys }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("#82BF35");
  const [error, setError] = useState("");

  const tryAdd = () => {
    const trimmed = name.trim();
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmed)) {
      setError(
        "Name must start with a letter and contain only letters, numbers, or underscores."
      );
      return;
    }
    if (existingKeys.includes(trimmed)) {
      setError("A color with that name already exists.");
      return;
    }
    if (!/^#[0-9a-fA-F]{6}$/.test(value)) {
      setError("Pick a valid hex color.");
      return;
    }
    onAdd(trimmed, value);
  };

  return (
    <div
      className="rounded-xl border p-3 space-y-2"
      style={{
        borderColor: THEME.border,
        backgroundColor: "rgba(255,255,255,0.75)",
      }}
    >
      <div className="text-sm font-extrabold" style={{ color: THEME.text }}>
        Add a color
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
          placeholder="Name (e.g. tealBase, accent)"
          className="flex-1 rounded border px-3 py-2 text-sm"
          style={{ borderColor: THEME.border }}
          spellCheck={false}
        />
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
          aria-label="New color value (picker)"
          className="w-12 h-10 rounded cursor-pointer border shrink-0"
          style={{ borderColor: THEME.border, padding: 0 }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            // Allow typing freely (incl. partial hex). Auto-prepend
            // '#' if the user starts with a hex digit.
            let v = e.target.value;
            if (v.length > 0 && v[0] !== "#") v = "#" + v;
            setValue(v);
            setError("");
          }}
          aria-label="New color hex value"
          placeholder="#RRGGBB"
          spellCheck={false}
          maxLength={7}
          className="w-24 rounded border px-2 py-2 text-sm font-mono shrink-0"
          style={{ borderColor: THEME.border }}
        />
        <button
          type="button"
          onClick={tryAdd}
          className="rounded-lg px-3 py-2 text-sm font-extrabold border transition hover:shadow-md"
          style={{
            backgroundColor: COLORS.greenBase,
            borderColor: THEME.border,
            color: "#ffffff",
          }}
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-2 text-sm font-extrabold border transition hover:bg-white"
          style={{
            borderColor: THEME.border,
            color: THEME.text,
            backgroundColor: "transparent",
          }}
        >
          Cancel
        </button>
      </div>
      <p className="text-xs" style={{ color: "rgba(31,56,0,0.6)" }}>
        Names ending in <code>Base</code>, <code>Light</code>, <code>Dark</code>,
        or <code>Deep</code> create groups. Below you can swap the color roles
      </p>
      {error ? <div className="text-xs text-red-700">{error}</div> : null}
    </div>
  );
}

// One row in the Roles panel. Shows a role's name, the swatch of the
// color currently assigned to it, and a type-to-search input bound to
// a <datalist> of every available color name. The input accepts
// typing OR clicking to expand the suggestions; invalid entries are
// reverted on blur.
function RoleRow({
  roleName,
  assignedColorKey,
  defaultColorKey,
  colors,
  onAssign,
  onReset,
}) {
  // Track what the user is currently typing separately from the
  // committed assignment so partial / invalid text can sit in the box
  // without triggering an assign on every keystroke.
  const [draft, setDraft] = useState(assignedColorKey);

  // If the assigned color changes externally (e.g. Reset, Save, or a
  // Firestore push), sync the visible input value back to it.
  useEffect(() => {
    setDraft(assignedColorKey);
  }, [assignedColorKey]);

  const isDefault = assignedColorKey === defaultColorKey;
  const swatch = colors[assignedColorKey] || "#cccccc";
  const listId = `theme-role-options-${roleName}`;

  // Commit the typed value: if it matches an existing color name,
  // assign it. Otherwise revert the input to the last-good value.
  const commit = () => {
    if (draft && draft !== assignedColorKey && colors[draft]) {
      onAssign(draft);
    } else if (!colors[draft]) {
      setDraft(assignedColorKey);
    }
  };

  return (
    <div
      className="flex items-center gap-3 rounded-lg border p-2"
      style={{
        borderColor: THEME.border,
        backgroundColor: "rgba(255,255,255,0.55)",
      }}
    >
      <div
        className="w-9 h-9 rounded border shrink-0"
        style={{ backgroundColor: swatch, borderColor: THEME.border }}
        title={swatch}
      />
      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-extrabold truncate"
          style={{ color: THEME.text }}
        >
          {roleName}
        </div>
        <div
          className="text-[10px] font-mono truncate"
          style={{ color: "rgba(31,56,0,0.55)" }}
        >
          → {assignedColorKey}
        </div>
      </div>
      <input
        type="text"
        list={listId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            setDraft(assignedColorKey);
            e.currentTarget.blur();
          }
        }}
        placeholder="Type or pick…"
        spellCheck={false}
        autoComplete="off"
        className="text-[11px] font-mono rounded border px-2 py-1 bg-white w-[120px]"
        style={{ borderColor: THEME.border }}
        title={`Type a color name or pick from the list to assign to "${roleName}"`}
      />
      <datalist id={listId}>
        {Object.keys(colors).map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
      <button
        type="button"
        onClick={onReset}
        disabled={isDefault}
        className={[
          "text-[10px] font-extrabold rounded px-2 py-1 border transition",
          isDefault ? "opacity-40 cursor-not-allowed" : "hover:bg-white",
        ].join(" ")}
        style={{
          borderColor: THEME.border,
          color: THEME.text,
          backgroundColor: "transparent",
        }}
        title={
          isDefault
            ? "Already at default"
            : `Reset to ${defaultColorKey}`
        }
      >
        Reset
      </button>
    </div>
  );
}

function ColorEditor() {
  // Live editing state. Initialized to the default palette; Firestore
  // values overwrite once they load. Tracks expanded-family booleans,
  // the add-color form's open/closed state, AND the current
  // role-to-color assignments.
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [expanded, setExpanded] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, "config", "theme"))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          // Saved doc holds palette keys at the top level and a nested
          // `roles` object. Pull them apart so each goes into its own
          // state slot.
          const data = snap.data() || {};
          const { roles: savedRoles, ...savedColors } = data;
          setColors({ ...DEFAULT_COLORS, ...savedColors });
          if (savedRoles && typeof savedRoles === "object") {
            setRoles({ ...DEFAULT_ROLES, ...savedRoles });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || "Failed to load theme");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(
    () => groupColorKeys(Object.keys(colors)),
    [colors]
  );

  const setOne = (key, value) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    applyColors({ [key]: value });
  };

  const resetOne = (key) => {
    if (key in DEFAULT_COLORS) setOne(key, DEFAULT_COLORS[key]);
  };

  const deleteOne = (key) => {
    if (key in DEFAULT_COLORS) return; // built-in: never deletable
    setColors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    document.documentElement.style.removeProperty(`--color-${key}`);
  };

  const resetAll = () => {
    Object.keys(colors).forEach((key) => {
      if (!(key in DEFAULT_COLORS)) {
        document.documentElement.style.removeProperty(`--color-${key}`);
      }
    });
    setColors(DEFAULT_COLORS);
    setRoles(DEFAULT_ROLES);
    applyColors(DEFAULT_COLORS);
    applyRoles(DEFAULT_ROLES);
  };

  const addCustom = (key, value) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    applyColors({ [key]: value });
    setAddingNew(false);
    // Open the new color's family card if it joined an existing one
    // (so the user immediately sees it sitting under its siblings).
    const { family } = colorFamilyInfo(key);
    setExpanded((prev) => ({ ...prev, [family]: true }));
  };

  // Assign a role to point at a different color. e.g.
  // setRole("backgroundBottom", "aquaBase") → backgroundBottom now
  // uses aquaBase's value. Updates the CSS variable for live preview.
  const setRole = (roleName, colorKey) => {
    setRoles((prev) => ({ ...prev, [roleName]: colorKey }));
    applyRoles({ [roleName]: colorKey });
  };

  const resetRole = (roleName) => {
    const def = DEFAULT_ROLES[roleName];
    if (def) setRole(roleName, def);
  };


  const toggleFamily = (familyName) => {
    setExpanded((prev) => ({ ...prev, [familyName]: !prev[familyName] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Persist both the palette and the role assignments in one doc.
      // Roles live under a nested "roles" key so applyColors can skip
      // them during palette application.
      await setDoc(doc(db, "config", "theme"), { ...colors, roles });
      setSavedAt(new Date());
    } catch (err) {
      alert("Could not save theme: " + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const existingKeys = Object.keys(colors);

  return (
    <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
      <CardHeader
        title="Site Colors"
        rightSlot={
          savedAt ? <Pill>Saved {savedAt.toLocaleTimeString()}</Pill> : null
        }
        icon={<IconPalette className="w-6 h-6" />}
      />

      <div className="px-5 pt-4 pb-2">
        <p className="text-sm" style={{ color: "rgba(31,56,0,0.66)" }}>
          Each card is a color family. Click <strong>variants</strong> to
          expand its lights, darks, and deeps. Changes preview instantly;
          click <strong>Save</strong> to push them live for everyone.
        </p>
        {loadError ? (
          <div className="mt-2 text-xs text-red-700">
            Couldn't load saved theme: {loadError}
          </div>
        ) : null}
      </div>

      <div className="flex-1 min-h-0 px-5 pb-3 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {groups.map((group) => (
            <ColorFamilyCard
              key={group.name}
              group={group}
              colors={colors}
              defaults={DEFAULT_COLORS}
              isExpanded={!!expanded[group.name]}
              onToggle={() => toggleFamily(group.name)}
              onChange={setOne}
              onReset={resetOne}
              onDelete={deleteOne}
            />
          ))}
        </div>

        <div className="mt-3">
          {addingNew ? (
            <AddColorForm
              onAdd={addCustom}
              onCancel={() => setAddingNew(false)}
              existingKeys={existingKeys}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingNew(true)}
              className="rounded-xl px-4 py-2 text-sm font-extrabold border transition hover:bg-white w-full sm:w-auto"
              style={{
                borderColor: THEME.border,
                color: THEME.text,
                backgroundColor: "transparent",
              }}
            >
              + Add a color
            </button>
          )}
        </div>

        <div className="mt-5">
          <div
            className="text-sm font-extrabold mb-1"
            style={{ color: THEME.text }}
          >
            Color roles
          </div>
          <p
            className="text-xs mb-2"
            style={{ color: "rgba(31,56,0,0.6)" }}
          >
            The site uses these roles
            in code (e.g. <code>backgroundBottom</code> drives the bottom of
            the page gradient). Re-assign a role to swap its color globally
            without renaming anything in code.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ROLE_KEYS.map((roleName) => (
              <RoleRow
                key={roleName}
                roleName={roleName}
                assignedColorKey={roles[roleName] || DEFAULT_ROLES[roleName]}
                defaultColorKey={DEFAULT_ROLES[roleName]}
                colors={colors}
                onAssign={(colorKey) => setRole(roleName, colorKey)}
                onReset={() => resetRole(roleName)}
              />
            ))}
          </div>
        </div>
      </div>

      <div
        className="px-5 py-3 border-t flex flex-wrap items-center gap-2"
        style={{ borderColor: THEME.border }}
      >
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className={[
            "rounded-xl px-4 py-2 text-sm font-extrabold border transition",
            saving ? "opacity-60 cursor-wait" : "hover:shadow-md",
          ].join(" ")}
          style={{
            backgroundColor: COLORS.greenBase,
            borderColor: THEME.border,
            color: "#ffffff",
          }}
        >
          {saving ? "Saving…" : "Save for everyone"}
        </button>
        <button
          type="button"
          onClick={resetAll}
          className="rounded-xl px-3 py-2 text-sm font-extrabold border transition hover:bg-white"
          style={{
            borderColor: THEME.border,
            color: THEME.text,
            backgroundColor: "transparent",
          }}
        >
          Reset all to defaults
        </button>
      </div>
    </CardShell>
  );
}

// ================================================================
// Admin page component
// ================================================================
export default function Admin() {
  const navigate = useNavigate();

  // Who's signed in (their uid) and their own user profile doc
  // (so we can check management/staff rights).
  const [currentUid, setCurrentUid] = useState(auth.currentUser?.uid || "");
  const [meDoc, setMeDoc] = useState(null);

  // Which top-level tab is showing + which Management sub-tab.
  const [activeTab, setActiveTab] = useState("inquiries");
  const [managementSubTab, setManagementSubTab] = useState("users");

  // All inquiries from Firestore (we sort/filter them in memory).
  const [rawInquiries, setRawInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(true);

  // Message threads (used to show which inquiries already have an
  // active conversation attached).
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);

  // All user profiles (Staff and Management tabs read from this).
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Inquiry types catalog (the dropdown options on the user-facing
  // inquiry page). Edited in the Management tab here.
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [autoBootstrappingTypes, setAutoBootstrappingTypes] = useState(false);

  const [selectedUnclaimedId, setSelectedUnclaimedId] = useState("");
  const [selectedMyId, setSelectedMyId] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedManagedUserId, setSelectedManagedUserId] = useState("");
  const [selectedInquiryTypeId, setSelectedInquiryTypeId] = useState("");
  const [isCreatingNewType, setIsCreatingNewType] = useState(false);

  const [presenceChoice, setPresenceChoice] = useState("online");
  const [managementSearch, setManagementSearch] = useState("");
  const [typeSearch, setTypeSearch] = useState("");

  // Narrows the Unclaimed Inquiries list to a single inquiry type.
  // Empty string means "show all types".
  const [unclaimedTypeFilter, setUnclaimedTypeFilter] = useState("");

  const [typeEditor, setTypeEditor] = useState({
    id: "",
    key: "",
    label: "",
    description: "",
    sortOrder: 9999,
    active: true,
    archived: false,
    autoOpenThread: true,
    defaultThreadMessage: "",
    followUpQuestions: [],
  });

  const [savingType, setSavingType] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [cleaningUpThreads, setCleaningUpThreads] = useState(false);
  const [lastCleanupResult, setLastCleanupResult] = useState(null);

  // Two important permission flags computed from the signed-in user:
  //   canManage     - their user doc has `management: true`. Unlocks
  //                   the Management tab and destructive actions like
  //                   delete-user, demote-admin, edit inquiry types.
  //   meIsStaffLike - staff OR management. Used to grant general
  //                   "can use admin tools" access.
  const canManage = meDoc?.management === true;
  const meIsStaffLike = isStaffLike(meDoc);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || "");
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUid) {
      setMeDoc(null);
      return;
    }

    const unsub = onSnapshot(
      doc(db, "users", currentUid),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setMeDoc({ uid: currentUid, ...data });
        if (data?.staffStatus) {
          setPresenceChoice(data.staffStatus);
        } else {
          setPresenceChoice("online");
        }
      },
      (err) => {
        console.error("Failed to load current user:", err);
        setMeDoc(null);
      }
    );

    return () => unsub();
  }, [currentUid]);

  // Live-subscribe to all inquiries in Firestore, newest first.
  // `onSnapshot` keeps the list in sync as inquiries come in / change
  // (no manual refresh needed). The "raw" name distinguishes this from
  // the filtered/sorted versions computed further down.
  useEffect(() => {
    const q = query(collection(db, "inquiries"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((docSnap) => {
          const d = docSnap.data() || {};
          const details = (d.details || "").toString();
          const from = d.createdByName || d.createdByEmail || d.createdByUid || "Unknown";

          const inquiryTypeText =
            d.inquiryTypeLabel ||
            d.categoryLabel ||
            (d.categoryMain
              ? d.categorySub
                ? `${String(d.categoryMain).replaceAll("_", " ")}: ${String(d.categorySub).replaceAll("_", " ")}`
                : String(d.categoryMain).replaceAll("_", " ")
              : d.category
              ? String(d.category).replaceAll("_", " ")
              : "—");

          const claimedByUid = d.claimedByUid || d.assignedToUid || "";
          const claimedByName =
            d.claimedByName ||
            d.assignedToName ||
            d.claimedByEmail ||
            d.assignedToEmail ||
            (claimedByUid ? "Claimed" : "");

          return {
            id: docSnap.id,
            from,
            subject: inquiryTypeText,
            last: details || "",
            createdAt: formatShortDate(d.createdAt),
            createdAtRaw: d.createdAt || null,
            status: d.status || "new",
            urgency: d.urgency || null,
            category: inquiryTypeText,
            inquiryTypeId: d.inquiryTypeId || "",
            inquiryTypeKey: d.inquiryTypeKey || "",
            inquiryTypeLabel: d.inquiryTypeLabel || inquiryTypeText,
            claimedByUid,
            claimedByName,
            createdByUid: d.createdByUid || "",
            threadId: d.threadId || "",
          };
        });

        setRawInquiries(items);
        setLoadingInquiries(false);
      },
      (err) => {
        console.error("Failed to load inquiries:", err);
        setRawInquiries([]);
        setLoadingInquiries(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "threads"), orderBy("updatedAt", "desc"), limit(150));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setThreads(list);
        setThreadsLoading(false);
      },
      (err) => {
        console.error("Failed to load threads:", err);
        setThreads([]);
        setThreadsLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const list = snap.docs.map((d) => ({ uid: d.id, ...(d.data() || {}) }));
        setUsers(list);
        setUsersLoading(false);
      },
      (err) => {
        console.error("Failed to load users:", err);
        setUsers([]);
        setUsersLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "inquiryTypes"),
      (snap) => {
        const list = snap.docs.map((d) => normalizeInquiryTypeForEditor({ id: d.id, ...(d.data() || {}) }));
        setInquiryTypes(buildInquiryTypeList(list));
        setTypesLoading(false);
      },
      (err) => {
        console.error("Failed to load inquiry types:", err);
        setInquiryTypes([]);
        setTypesLoading(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    if (activeTab === "management" && !canManage) {
      setActiveTab("inquiries");
    }
  }, [activeTab, canManage]);

  useEffect(() => {
    if (!canManage) return;
    if (typesLoading) return;
    if (autoBootstrappingTypes) return;
    if (inquiryTypes.length > 0) return;

    let cancelled = false;

    async function bootstrapInquiryTypes() {
      try {
        setAutoBootstrappingTypes(true);

        const snap = await fetchDocs(collection(db, "inquiryTypes"));
        if (!snap.empty || cancelled) return;

        for (const item of DEFAULT_INQUIRY_TYPES) {
          await setDoc(
            doc(db, "inquiryTypes", item.key),
            {
              ...item,
              archived: item.archived === true,
              updatedAt: serverTimestamp(),
              updatedByUid: auth.currentUser?.uid || null,
            },
            { merge: true }
          );
        }
      } catch (err) {
        console.error("Failed to auto-bootstrap inquiry types:", err);
      } finally {
        if (!cancelled) setAutoBootstrappingTypes(false);
      }
    }

    bootstrapInquiryTypes();

    return () => {
      cancelled = true;
    };
  }, [canManage, typesLoading, inquiryTypes, autoBootstrappingTypes]);

  const updateMyPresence = useCallback(async (nextStatus, extra = {}) => {
    const me = auth.currentUser;
    if (!me?.uid) return;

    try {
      await updateDoc(doc(db, "users", me.uid), {
        staffStatus: nextStatus,
        lastSeenAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
        ...extra,
      });
    } catch (e) {
      console.error("Failed to update staff status:", e);
    }
  }, []);

  useEffect(() => {
    if (!currentUid || !meIsStaffLike) return;

    let cancelled = false;

    const pushOnlineIfNeeded = async () => {
      if (cancelled) return;
      const hidden = document.visibilityState === "hidden";
      const desired = hidden ? "away" : (presenceChoice === "away" ? "away" : "online");
      await updateMyPresence(desired);
    };

    pushOnlineIfNeeded();

    const heartbeat = setInterval(() => {
      if (document.visibilityState === "visible") {
        updateMyPresence(presenceChoice === "away" ? "away" : "online");
      }
    }, 60000);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        updateMyPresence("away");
      } else {
        updateMyPresence(presenceChoice === "away" ? "away" : "online");
      }
    };

    window.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      window.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentUid, meIsStaffLike, presenceChoice, updateMyPresence]);

  const inquiriesSorted = useMemo(() => {
    const copy = [...rawInquiries];
    copy.sort((a, b) => {
      const urA = urgencyRank(a.urgency);
      const urB = urgencyRank(b.urgency);
      if (urA !== urB) return urB - urA;

      const ta = a.createdAtRaw?.toMillis ? a.createdAtRaw.toMillis() : 0;
      const tb = b.createdAtRaw?.toMillis ? b.createdAtRaw.toMillis() : 0;
      return tb - ta;
    });
    return copy;
  }, [rawInquiries]);

  const activeInquiries = useMemo(
    () => inquiriesSorted.filter((i) => !isClosedStatus(i.status)),
    [inquiriesSorted]
  );

  const unclaimedInquiries = useMemo(
    () => activeInquiries.filter((i) => !i.claimedByUid),
    [activeInquiries]
  );

  // Distinct inquiry type labels that appear in the current
  // unclaimed list — used to populate the Type filter dropdown.
  // We pull from unclaimedInquiries (not the global type catalog)
  // so the dropdown only offers types you can actually pick.
  const unclaimedTypeOptions = useMemo(() => {
    const seen = new Set();
    unclaimedInquiries.forEach((i) => {
      const label = (i.inquiryTypeLabel || i.category || "").trim();
      if (label) seen.add(label);
    });
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [unclaimedInquiries]);

  // Narrows the unclaimed list down to the selected inquiry type.
  // When no type is selected, the list passes through unchanged
  // (keeping the urgency-then-recency order from inquiriesSorted).
  const filteredUnclaimedInquiries = useMemo(() => {
    if (!unclaimedTypeFilter) return unclaimedInquiries;
    return unclaimedInquiries.filter(
      (i) =>
        (i.inquiryTypeLabel || i.category || "").trim() === unclaimedTypeFilter
    );
  }, [unclaimedInquiries, unclaimedTypeFilter]);

  const myInquiries = useMemo(
    () => activeInquiries.filter((i) => i.claimedByUid && i.claimedByUid === currentUid),
    [activeInquiries, currentUid]
  );

  const staffUsers = useMemo(() => {
    return [...users]
      .filter((u) => isStaffLike(u))
      .sort((a, b) => {
        const sa = getEffectiveStaffStatus(a);
        const sb = getEffectiveStaffStatus(b);
        const statusRank = { online: 3, away: 2, offline: 1 };
        if ((statusRank[sb] || 0) !== (statusRank[sa] || 0)) {
          return (statusRank[sb] || 0) - (statusRank[sa] || 0);
        }
        return getUserDisplayName(a).localeCompare(getUserDisplayName(b));
      });
  }, [users]);

  const managedUsers = useMemo(() => {
    const term = managementSearch.trim().toLowerCase();
    return [...users]
      .filter((u) => {
        if (!term) return true;
        const hay = [
          getUserDisplayName(u),
          u.email || "",
          u.firstName || "",
          u.lastName || "",
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(term);
      })
      .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
  }, [users, managementSearch]);

  const filteredInquiryTypes = useMemo(() => {
    const term = typeSearch.trim().toLowerCase();

    return inquiryTypes.filter((item) => {
      if (item.archived === true) return false;

      if (!term) return true;

      const hay = [
        item.label || "",
        item.key || "",
        item.description || "",
        ...(item.followUpQuestions || []),
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(term);
    });
  }, [inquiryTypes, typeSearch]);

  useEffect(() => {
    if (!selectedUnclaimedId && filteredUnclaimedInquiries.length > 0) {
      setSelectedUnclaimedId(filteredUnclaimedInquiries[0].id);
    }
    if (selectedUnclaimedId && !filteredUnclaimedInquiries.some((x) => x.id === selectedUnclaimedId)) {
      setSelectedUnclaimedId(filteredUnclaimedInquiries[0]?.id || "");
    }
  }, [filteredUnclaimedInquiries, selectedUnclaimedId]);

  useEffect(() => {
    if (selectedMyId && !myInquiries.some((x) => x.id === selectedMyId)) {
      setSelectedMyId("");
    }
  }, [myInquiries, selectedMyId]);

  useEffect(() => {
    if (!selectedStaffId && staffUsers.length > 0) {
      setSelectedStaffId(staffUsers[0].uid);
    }
    if (selectedStaffId && !staffUsers.some((x) => x.uid === selectedStaffId)) {
      setSelectedStaffId(staffUsers[0]?.uid || "");
    }
  }, [staffUsers, selectedStaffId]);

  useEffect(() => {
    if (!selectedManagedUserId && managedUsers.length > 0) {
      setSelectedManagedUserId(managedUsers[0].uid);
    }
    if (selectedManagedUserId && !managedUsers.some((x) => x.uid === selectedManagedUserId)) {
      setSelectedManagedUserId(managedUsers[0]?.uid || "");
    }
  }, [managedUsers, selectedManagedUserId]);

  useEffect(() => {
    if (isCreatingNewType) return;

    if (!selectedInquiryTypeId && filteredInquiryTypes.length > 0) {
      setSelectedInquiryTypeId(filteredInquiryTypes[0].id);
    }
    if (selectedInquiryTypeId && !filteredInquiryTypes.some((x) => x.id === selectedInquiryTypeId)) {
      setSelectedInquiryTypeId(filteredInquiryTypes[0]?.id || "");
    }
  }, [filteredInquiryTypes, selectedInquiryTypeId, isCreatingNewType]);

  const selectedUnclaimed = useMemo(
    () => unclaimedInquiries.find((x) => x.id === selectedUnclaimedId) || null,
    [unclaimedInquiries, selectedUnclaimedId]
  );

  const selectedMine = useMemo(
    () => myInquiries.find((x) => x.id === selectedMyId) || null,
    [myInquiries, selectedMyId]
  );

  const selectedStaff = useMemo(
    () => staffUsers.find((x) => x.uid === selectedStaffId) || null,
    [staffUsers, selectedStaffId]
  );

  const selectedManagedUser = useMemo(
    () => managedUsers.find((x) => x.uid === selectedManagedUserId) || null,
    [managedUsers, selectedManagedUserId]
  );

  const selectedInquiryType = useMemo(
    () => inquiryTypes.find((x) => x.id === selectedInquiryTypeId) || null,
    [inquiryTypes, selectedInquiryTypeId]
  );

  useEffect(() => {
    if (isCreatingNewType) return;
    if (!selectedInquiryType) return;
    setTypeEditor(normalizeInquiryTypeForEditor(selectedInquiryType));
  }, [selectedInquiryType, isCreatingNewType]);

  const claimInquiry = useCallback(async (inquiryId) => {
    const me = auth.currentUser;
    if (!me?.uid) {
      alert("You must be signed in as staff.");
      return;
    }

    try {
      const inquiryRef = doc(db, "inquiries", inquiryId);
      const snap = await getDoc(inquiryRef);
      if (!snap.exists()) {
        alert("Inquiry not found.");
        return;
      }

      const d = snap.data() || {};
      if (isClosedStatus(d.status)) {
        alert("This inquiry is closed.");
        return;
      }

      const alreadyClaimed = d.claimedByUid || d.assignedToUid;
      if (alreadyClaimed) {
        if (alreadyClaimed === me.uid) return;
        alert("This inquiry is already claimed by another staff member.");
        return;
      }

      await updateDoc(inquiryRef, {
        claimedByUid: me.uid,
        claimedByName: me.displayName || getUserDisplayName(meDoc) || null,
        claimedByEmail: me.email || null,
        claimedAt: serverTimestamp(),
        status: d.status === "new" ? "open" : d.status || "open",
      });

      setSelectedMyId(inquiryId);
      setActiveTab("inquiries");
    } catch (e) {
      console.error("Claim failed:", e);
      alert("Failed to claim inquiry. Please try again.");
    }
  }, [meDoc]);

  const unclaimInquiry = useCallback(async (inquiryId) => {
    const me = auth.currentUser;
    if (!me?.uid) {
      alert("You must be signed in as staff.");
      return;
    }

    try {
      const inquiryRef = doc(db, "inquiries", inquiryId);
      const snap = await getDoc(inquiryRef);
      if (!snap.exists()) {
        alert("Inquiry not found.");
        return;
      }

      const d = snap.data() || {};
      if (isClosedStatus(d.status)) {
        alert("This inquiry is closed.");
        return;
      }

      const claimedUid = d.claimedByUid || d.assignedToUid || "";
      if (claimedUid && claimedUid !== me.uid) {
        alert("Only the staff member who claimed this can unclaim it.");
        return;
      }

      await updateDoc(inquiryRef, {
        claimedByUid: null,
        claimedByName: null,
        claimedByEmail: null,
        claimedAt: null,
        status: "new",
      });

      setSelectedMyId("");
      setSelectedUnclaimedId(inquiryId);
    } catch (e) {
      console.error("Unclaim failed:", e);
      alert("Failed to unclaim inquiry. Please try again.");
    }
  }, []);

  const closeInquiry = useCallback(async (inquiryId) => {
    const me = auth.currentUser;
    if (!me?.uid) {
      alert("You must be signed in as staff.");
      return;
    }

    try {
      const inquiryRef = doc(db, "inquiries", inquiryId);
      const snap = await getDoc(inquiryRef);
      if (!snap.exists()) {
        alert("Inquiry not found.");
        return;
      }

      const d = snap.data() || {};
      if (isClosedStatus(d.status)) return;

      const claimedUid = d.claimedByUid || d.assignedToUid || "";
      if (claimedUid && claimedUid !== me.uid) {
        alert("Only the staff member who claimed this can close it.");
        return;
      }

      if (!claimedUid) {
        alert("Claim the inquiry before closing it.");
        return;
      }

      await updateDoc(inquiryRef, {
        status: "resolved",
        statusUpdatedAt: serverTimestamp(),
        resolvedAt: serverTimestamp(),
      });

      setSelectedMyId("");
    } catch (e) {
      console.error("Close failed:", e);
      alert("Failed to close inquiry. Please try again.");
    }
  }, []);

  const openOrCreateThreadFromInquiry = useCallback(
    async (inquiryId) => {
      const me = auth.currentUser;
      if (!me?.uid) {
        alert("You must be signed in as staff.");
        return;
      }

      const inquiryRef = doc(db, "inquiries", inquiryId);
      const inquirySnap = await getDoc(inquiryRef);
      if (!inquirySnap.exists()) {
        alert("Inquiry not found.");
        return;
      }

      const inquiry = inquirySnap.data() || {};
      if (isClosedStatus(inquiry.status)) {
        alert("This inquiry is closed.");
        return;
      }

      let userUid = inquiry.createdByUid || inquiry.createdBy || inquiry.userUid || "";

      if (!userUid) {
        const email = inquiry.createdByEmail || inquiry.email || inquiry.createdBy || "";
        if (email) {
          const qs = query(collection(db, "users"), where("email", "==", email), limit(1));
          const res = await getDocs(qs);
          if (!res.empty) userUid = res.docs[0].id;
        }
      }

      if (!userUid) {
        alert("Can't find the inquiry maker UID.");
        return;
      }

      if (inquiry.threadId) {
        navigate("/messages", { state: { threadId: inquiry.threadId, adminView: true } });
        return;
      }

      const title = `Support: ${inquiry.inquiryTypeLabel || inquiry.categoryLabel || inquiry.categoryMain || inquiry.category || "Inquiry"}`;

      const threadDoc = await addDoc(collection(db, "threads"), {
        title,
        inquiryId,
        inquiryTypeId: inquiry.inquiryTypeId || "",
        inquiryTypeKey: inquiry.inquiryTypeKey || "",
        inquiryTypeLabel: inquiry.inquiryTypeLabel || "",
        createdForUid: userUid,
        participants: Array.from(new Set([userUid, me.uid].filter(Boolean))),
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: "",
        lastSenderUid: "",
      });

      const seedText = "Staff started a support thread for this inquiry.";
      await addDoc(collection(db, "threads", threadDoc.id, "messages"), {
        text: seedText,
        senderUid: me.uid,
        senderRole: "admin",
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "threads", threadDoc.id), {
        lastMessage: seedText,
        lastSenderUid: me.uid,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(inquiryRef, {
        threadId: threadDoc.id,
        threadStatus: "open",
      });

      navigate("/messages", { state: { threadId: threadDoc.id, adminView: true } });
    },
    [navigate]
  );

  const threadForInquiry = useCallback(
    (inquiryId, threadId) => {
      if (threadId) return threads.find((t) => t.id === threadId) || null;
      return threads.find((t) => t.inquiryId === inquiryId) || null;
    },
    [threads]
  );

  const setMyStatusChoice = useCallback(
    async (next) => {
      setPresenceChoice(next);
      await updateMyPresence(next);
    },
    [updateMyPresence]
  );

  const toggleUserRole = useCallback(
    async (userId, field, currentValue) => {
      if (!canManage) {
        alert("You do not have management access.");
        return;
      }

      if (!userId) return;

      if (userId === currentUid && field === "management") {
        alert("You cannot change your own management access here.");
        return;
      }

      try {
        await updateDoc(doc(db, "users", userId), {
          [field]: !currentValue,
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error(`Failed to update ${field}:`, e);
        alert(`Failed to update ${field}.`);
      }
    },
    [canManage, currentUid]
  );

  const deleteManagedUser = useCallback(async () => {
    if (!canManage) {
      alert("You do not have management access.");
      return;
    }

    if (!selectedManagedUser?.uid) {
      alert("Select a user first.");
      return;
    }

    if (selectedManagedUser.uid === currentUid) {
      alert("You cannot delete your own account from this panel.");
      return;
    }

    const userName = getUserDisplayName(selectedManagedUser);
    const userEmail = selectedManagedUser.email || "No email listed";

    const confirmed = window.confirm(
      `Delete this user's account?\n\n${userName}\n${userEmail}\n\nThis removes their Firebase Auth login and deletes their user profile document. This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingUser(true);

      const deleteUserAccount = httpsCallable(functions, "deleteManagedUserAccount");
      await deleteUserAccount({
        uid: selectedManagedUser.uid,
      });

      setSelectedManagedUserId("");
      alert("User account deleted.");
    } catch (error) {
      console.error("Failed to delete user account:", error);
      alert(error?.message || "Failed to delete user account.");
    } finally {
      setDeletingUser(false);
    }
  }, [canManage, selectedManagedUser, currentUid]);

  const handleCleanupOrphanThreads = useCallback(async () => {
    if (!canManage) return;

    const confirmed = window.confirm(
      "Delete thread documents that have no messages?\n\nThese are usually leftovers from failed inquiry submissions. Threads created in the last 5 minutes are skipped."
    );

    if (!confirmed) return;

    try {
      setCleaningUpThreads(true);
      setLastCleanupResult(null);

      const cleanup = httpsCallable(functions, "cleanupOrphanThreads");
      const result = await cleanup();

      setLastCleanupResult(result?.data || null);
    } catch (error) {
      console.error("Failed to clean up orphan threads:", error);
      alert(error?.message || "Failed to clean up orphan threads.");
    } finally {
      setCleaningUpThreads(false);
    }
  }, [canManage]);

  const updateTypeEditorField = useCallback((field, value) => {
    setTypeEditor((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateTypeEditorQuestionsFromText = useCallback((value) => {
    const lines = String(value)
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    setTypeEditor((prev) => ({
      ...prev,
      followUpQuestions: lines,
    }));
  }, []);

  const createNewBlankType = useCallback(() => {
    const nextOrder =
      inquiryTypes.length > 0
        ? Math.max(...inquiryTypes.map((t) => Number.isFinite(Number(t.sortOrder)) ? Number(t.sortOrder) : 0)) + 1
        : 1;

    setIsCreatingNewType(true);
    setSelectedInquiryTypeId("");
    setTypeEditor({
      id: "",
      key: "",
      label: "",
      description: "",
      sortOrder: nextOrder,
      active: true,
      archived: false,
      autoOpenThread: true,
      defaultThreadMessage: "",
      followUpQuestions: [],
    });
  }, [inquiryTypes]);

  const saveInquiryType = useCallback(async () => {
    if (!canManage) {
      alert("You do not have management access.");
      return;
    }

    const key = slugifyInquiryTypeKey(typeEditor.key || typeEditor.label);
    const label = String(typeEditor.label || "").trim();

    if (!key) {
      alert("Please enter a label or key.");
      return;
    }

    if (!label) {
      alert("Please enter a label.");
      return;
    }

    const docId = typeEditor.id || key;

    try {
      setSavingType(true);

      const payload = {
        key,
        label,
        description: String(typeEditor.description || "").trim(),
        sortOrder: Number.isFinite(Number(typeEditor.sortOrder)) ? Number(typeEditor.sortOrder) : 9999,
        active: typeEditor.active !== false,
        archived: typeEditor.archived === true,
        autoOpenThread: typeEditor.autoOpenThread === true,
        defaultThreadMessage: String(typeEditor.defaultThreadMessage || "").trim(),
        followUpQuestions: Array.isArray(typeEditor.followUpQuestions)
          ? typeEditor.followUpQuestions.map((q) => String(q).trim()).filter(Boolean)
          : [],
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };

      if (!typeEditor.id) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(doc(db, "inquiryTypes", docId), payload, { merge: true });
      setSelectedInquiryTypeId(docId);
      setIsCreatingNewType(false);
    } catch (e) {
      console.error("Failed to save inquiry type:", e);
      alert("Failed to save inquiry type.");
    } finally {
      setSavingType(false);
    }
  }, [canManage, typeEditor]);

  const archiveInquiryType = useCallback(async () => {
    if (!canManage) {
      alert("You do not have management access.");
      return;
    }

    if (!typeEditor.id) {
      alert("Save the inquiry type before archiving it.");
      return;
    }

    try {
      await updateDoc(doc(db, "inquiryTypes", typeEditor.id), {
        archived: true,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      });

      setIsCreatingNewType(false);
      setSelectedInquiryTypeId("");
      setTypeEditor({
        id: "",
        key: "",
        label: "",
        description: "",
        sortOrder: 9999,
        active: true,
        archived: false,
        autoOpenThread: true,
        defaultThreadMessage: "",
        followUpQuestions: [],
      });
    } catch (e) {
      console.error("Failed to archive inquiry type:", e);
      alert("Failed to archive inquiry type.");
    }
  }, [canManage, typeEditor.id]);

  const openExistingInquiryType = useCallback((id) => {
    setIsCreatingNewType(false);
    setSelectedInquiryTypeId(id);
  }, []);

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden min-h-0"
      style={pageBackground}
    >
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img
              src={logo}
              alt="Logo"
              className="w-13 h-12 object-contain"
            />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Staff Portal
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate("/home")}
            className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            type="button"
          >
            Home
          </button>
        </div>
      </header>

      {/* --- Main body of the admin portal. The whole portal uses an
              "app-shell" layout: header up top, content fills the rest
              of the screen, no page-level scroll. Each tab manages
              its own internal scrolling. --- */}
      <main className="w-full max-w-[1500px] mx-auto px-4 sm:px-6 py-6 flex-1 min-h-0">
        <div className="h-full min-h-0 flex flex-col gap-4">
          {/* The top tabs (Inquiries / Staff Status / Management).
              The Management tab is only visible to users with
              management = true. */}
          <div className="flex flex-wrap items-center gap-2">
            <TabButton active={activeTab === "inquiries"} onClick={() => setActiveTab("inquiries")}>
              Inquiries
            </TabButton>

            <TabButton active={activeTab === "staff"} onClick={() => setActiveTab("staff")}>
              Staff Status
            </TabButton>

            {canManage ? (
              <TabButton active={activeTab === "management"} onClick={() => setActiveTab("management")}>
                Management
              </TabButton>
            ) : null}
          </div>

          {/* --- INQUIRIES TAB --- Two-column layout: the inquiry list
                  on the left, the detail panel for the selected
                  inquiry on the right. --- */}
          {activeTab === "inquiries" ? (
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.45fr)_380px] gap-6 h-full min-h-0">
              <section className="min-h-0 h-full">
                <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                  <CardHeader
                    title="Unclaimed Inquiries"
                    rightSlot={
                      <>
                        <select
                          value={unclaimedTypeFilter}
                          onChange={(e) => setUnclaimedTypeFilter(e.target.value)}
                          aria-label="Filter unclaimed inquiries by inquiry type"
                          className="text-[12px] font-extrabold rounded-xl border px-2 py-1 bg-white cursor-pointer max-w-[200px]"
                          style={{ color: THEME.text, borderColor: THEME.border }}
                        >
                          <option value="">Inquiry type: All</option>
                          {unclaimedTypeOptions.map((label) => (
                            <option key={label} value={label}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <Pill>
                          {loadingInquiries
                            ? "Loading..."
                            : unclaimedTypeFilter
                            ? `${filteredUnclaimedInquiries.length} / ${unclaimedInquiries.length}`
                            : `${unclaimedInquiries.length}`}
                        </Pill>
                      </>
                    }
                    icon={<IconInquiry className="w-6 h-6" />}
                  />

                  <div className="flex-1 min-h-0 p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4">
                    <SoftPanel className="min-h-0 max-h-[65vh] lg:max-h-none">
                      <div className="h-full max-h-[60vh] lg:max-h-full overflow-y-auto pr-1 space-y-2" style={{ scrollbarGutter: "stable" }}>
                        {loadingInquiries ? (
                          <EmptyBox label="Loading inquiries..." />
                        ) : filteredUnclaimedInquiries.length === 0 ? (
                          <EmptyBox
                            label={
                              unclaimedTypeFilter
                                ? `No unclaimed inquiries of type "${unclaimedTypeFilter}".`
                                : "No unclaimed inquiries."
                            }
                          />
                        ) : (
                          filteredUnclaimedInquiries.map((i) => (
                            <ListRow
                              key={i.id}
                              active={i.id === selectedUnclaimedId}
                              onClick={() => setSelectedUnclaimedId(i.id)}
                              title={i.subject}
                              subtitle={i.from}
                              meta={i.createdAt}
                              right={<UrgencyPill urgency={i.urgency} />}
                            />
                          ))
                        )}
                      </div>
                    </SoftPanel>

                    <div className="min-h-0 overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }}>
                      <SectionCard>
                        {selectedUnclaimed ? (
                          <>
                            <div className="text-lg font-extrabold" style={{ color: THEME.text }}>
                              {selectedUnclaimed.subject}
                            </div>

                            <div className="text-sm text-[rgba(31,56,0,0.62)] mt-1">
                              {selectedUnclaimed.from} • {selectedUnclaimed.createdAt}
                            </div>

                            <InquiryMetaRow inquiry={selectedUnclaimed} />

                            <div className="mt-5">
                              <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                Details
                              </div>
                              <div className="mt-2 text-[15px] text-[rgba(31,56,0,0.72)] leading-relaxed whitespace-pre-line">
                                {selectedUnclaimed.last || "—"}
                              </div>
                            </div>

                            <GreenButton className="mt-5 w-full" onClick={() => claimInquiry(selectedUnclaimed.id)}>
                              Claim Inquiry
                            </GreenButton>
                          </>
                        ) : (
                          <div className="text-sm text-[rgba(31,56,0,0.62)]">Select an inquiry.</div>
                        )}
                      </SectionCard>
                    </div>
                  </div>
                </CardShell>
              </section>

              <section className="min-h-0 h-full">
                <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                  <CardHeader
                    title="My Inquiries"
                    rightSlot={<Pill>{loadingInquiries ? "Loading..." : `${myInquiries.length}`}</Pill>}
                    icon={<IconMessages className="w-6 h-6" />}
                  />

                  <div className="flex-1 min-h-0 p-5 flex flex-col gap-4">
                    <SoftPanel className="min-h-0 flex-1 max-h-[65vh] xl:max-h-none">
                      <div className="h-full max-h-[60vh] xl:max-h-full overflow-y-auto pr-1 space-y-2" style={{ scrollbarGutter: "stable" }}>
                        {loadingInquiries ? (
                          <EmptyBox label="Loading..." />
                        ) : myInquiries.length === 0 ? (
                          <EmptyBox label="You have no claimed inquiries." />
                        ) : (
                          myInquiries.map((i) => (
                            <ListRow
                              key={i.id}
                              active={i.id === selectedMyId}
                              onClick={() => setSelectedMyId(i.id)}
                              title={i.subject}
                              subtitle={i.from}
                              meta={i.createdAt}
                              right={<StatusPill status={i.status} />}
                            />
                          ))
                        )}
                      </div>
                    </SoftPanel>

                    {selectedMine ? (
                      <div className="shrink-0 max-h-[38vh] overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }}>
                        <SectionCard>
                          <div className="text-base font-extrabold leading-tight" style={{ color: THEME.text }}>
                            {selectedMine.subject}
                          </div>

                          <div className="text-sm text-[rgba(31,56,0,0.62)] mt-1">
                            {selectedMine.from} • {selectedMine.createdAt}
                          </div>

                          <InquiryMetaRow inquiry={selectedMine} showClaimed />

                          <div className="mt-5">
                            <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                              Details
                            </div>
                            <div className="mt-2 text-sm text-[rgba(31,56,0,0.72)] leading-relaxed whitespace-pre-line">
                              {selectedMine.last || "—"}
                            </div>
                          </div>

                          <div className="mt-5 rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                            <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                              Thread
                            </div>

                            {threadsLoading ? (
                              <div className="mt-2 text-sm text-[rgba(31,56,0,0.62)]">Loading thread info…</div>
                            ) : (() => {
                                const t = threadForInquiry(selectedMine.id, selectedMine.threadId);
                                if (!t) return <div className="mt-2 text-sm text-[rgba(31,56,0,0.62)]">No thread yet.</div>;
                                return (
                                  <div className="mt-2 text-sm text-[rgba(31,56,0,0.70)]">
                                    <div className="font-extrabold" style={{ color: THEME.text }}>
                                      {t.title || "Thread"}
                                    </div>
                                    <div className="mt-1 text-[rgba(31,56,0,0.62)] break-words">
                                      {(t.lastMessage || "—").toString()}
                                    </div>
                                  </div>
                                );
                              })()}
                          </div>

                          <div className="mt-5 flex flex-col gap-2">
                            <GreenButton className="w-full" onClick={() => openOrCreateThreadFromInquiry(selectedMine.id)}>
                              <span className="inline-flex items-center gap-2">
                                <IconMessages className="w-4 h-4" />
                                {selectedMine.threadId ? "Open Thread" : "Create Thread"}
                              </span>
                            </GreenButton>

                            <GreenButton className="w-full" onClick={() => unclaimInquiry(selectedMine.id)}>
                              Unclaim
                            </GreenButton>

                            <GreenButton className="w-full" onClick={() => closeInquiry(selectedMine.id)}>
                              Close
                            </GreenButton>
                          </div>
                        </SectionCard>
                      </div>
                    ) : (
                      <SectionCard className="shrink-0">
                        <div className="text-sm text-[rgba(31,56,0,0.62)]">Select one of your inquiries to view details.</div>
                      </SectionCard>
                    )}
                  </div>
                </CardShell>
              </section>
            </div>
          ) : null}

          {/* --- STAFF TAB --- Left list: every staff user with their
                  online/offline status. Right panel: details for the
                  selected staff member and presence controls. --- */}
          {activeTab === "staff" ? (
            <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-6 h-full min-h-0">
              <section className="min-h-0 h-full">
                <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                  <CardHeader
                    title="My Status"
                    rightSlot={<PresencePill status={getEffectiveStaffStatus(meDoc)} />}
                    icon={<IconUsers className="w-6 h-6" />}
                  />

                  <div className="flex-1 min-h-0 p-5 overflow-y-auto">
                    <SectionCard>
                      <div className="text-lg font-extrabold" style={{ color: THEME.text }}>
                        {getUserDisplayName(meDoc)}
                      </div>

                      <div className="text-sm text-[rgba(31,56,0,0.62)] mt-1">{meDoc?.email || "—"}</div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {meDoc?.management ? <Pill>Management</Pill> : null}
                        {meDoc?.staff ? <Pill>Staff</Pill> : null}
                      </div>

                      <div className="mt-5">
                        <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                          Current Presence
                        </div>
                        <div className="mt-2">
                          <PresencePill status={getEffectiveStaffStatus(meDoc)} />
                        </div>
                      </div>

                      <div className="mt-5">
                        <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                          Update Status
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <GreenButton
                            className="w-full"
                            onClick={() => setMyStatusChoice("online")}
                            disabled={!meIsStaffLike}
                          >
                            Set Online
                          </GreenButton>

                          <SecondaryButton
                            className="w-full"
                            onClick={() => setMyStatusChoice("away")}
                            disabled={!meIsStaffLike}
                          >
                            Set Away
                          </SecondaryButton>
                        </div>

                        <div className="mt-3 text-xs text-[rgba(31,56,0,0.50)]">
                          Offline is handled automatically when activity becomes stale.
                        </div>
                      </div>

                      <div className="mt-5 rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                        <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                          Last Seen
                        </div>
                        <div className="mt-2 text-sm text-[rgba(31,56,0,0.70)]">
                          {formatDateTime(meDoc?.lastSeenAt)}
                        </div>
                      </div>
                    </SectionCard>
                  </div>
                </CardShell>
              </section>

              <section className="min-h-0 h-full">
                <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                  <CardHeader
                    title="Staff Directory"
                    rightSlot={<Pill>{usersLoading ? "Loading..." : `${staffUsers.length}`}</Pill>}
                    icon={<IconUsers className="w-6 h-6" />}
                  />

                  <div className="flex-1 min-h-0 p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4">
                    <SoftPanel className="min-h-0">
                      <div className="h-full overflow-y-auto pr-1 space-y-2" style={{ scrollbarGutter: "stable" }}>
                        {usersLoading ? (
                          <EmptyBox label="Loading staff..." />
                        ) : staffUsers.length === 0 ? (
                          <EmptyBox label="No staff found." />
                        ) : (
                          staffUsers.map((u) => (
                            <ListRow
                              key={u.uid}
                              active={u.uid === selectedStaffId}
                              onClick={() => setSelectedStaffId(u.uid)}
                              title={getUserDisplayName(u)}
                              subtitle={u.email || "—"}
                              meta=""
                              right={<PresencePill status={getEffectiveStaffStatus(u)} />}
                            />
                          ))
                        )}
                      </div>
                    </SoftPanel>

                    <div className="min-h-0 overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }}>
                      <SectionCard>
                        {selectedStaff ? (
                          <>
                            <div className="text-lg font-extrabold" style={{ color: THEME.text }}>
                              {getUserDisplayName(selectedStaff)}
                            </div>

                            <div className="text-sm text-[rgba(31,56,0,0.62)] mt-1">
                              {selectedStaff.email || "—"}
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <PresencePill status={getEffectiveStaffStatus(selectedStaff)} />
                              {selectedStaff.management ? <Pill>Management</Pill> : null}
                              {selectedStaff.staff ? <Pill>Staff</Pill> : null}
                            </div>

                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                                <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                  Last Active
                                </div>
                                <div className="mt-2 text-sm text-[rgba(31,56,0,0.70)]">
                                  {formatDateTime(selectedStaff.lastActiveAt)}
                                </div>
                              </div>

                              <div className="rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                                <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                  Last Seen
                                </div>
                                <div className="mt-2 text-sm text-[rgba(31,56,0,0.70)]">
                                  {formatDateTime(selectedStaff.lastSeenAt)}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-[rgba(31,56,0,0.62)]">Select a staff member.</div>
                        )}
                      </SectionCard>
                    </div>
                  </div>
                </CardShell>
              </section>
            </div>
          ) : null}

          {/* --- MANAGEMENT TAB --- Only visible to users with
                  management = true. Has its own sub-tabs (Users vs
                  Inquiry Types). Users sub-tab lets you toggle
                  approve/staff/management flags on other accounts.
                  Inquiry Types sub-tab lets you add/edit the topics
                  users can pick from on the inquiry page. --- */}
          {activeTab === "management" && canManage ? (
            <div className="h-full min-h-0 flex flex-col gap-4">
              <div
                className="rounded-2xl border p-2 inline-flex flex-wrap items-center gap-2 self-start shadow-sm backdrop-blur"
                style={{
                  backgroundColor: THEME.cardBgSoft,
                  borderColor: THEME.border,
                  boxShadow: "0 8px 18px rgba(31,56,0,0.08)",
                }}
              >
                <ManagementSubTab
                  active={managementSubTab === "users"}
                  onClick={() => setManagementSubTab("users")}
                >
                  User Access
                </ManagementSubTab>

                <ManagementSubTab
                  active={managementSubTab === "types"}
                  onClick={() => setManagementSubTab("types")}
                >
                  Inquiry Types
                </ManagementSubTab>

                <ManagementSubTab
                  active={managementSubTab === "theme"}
                  onClick={() => setManagementSubTab("theme")}
                >
                  Theme
                </ManagementSubTab>

                <ManagementSubTab
                  active={managementSubTab === "maintenance"}
                  onClick={() => setManagementSubTab("maintenance")}
                >
                  Maintenance
                </ManagementSubTab>

                {autoBootstrappingTypes ? <Pill>Setting up inquiry types…</Pill> : null}
              </div>

              {managementSubTab === "users" ? (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)] gap-6 h-full min-h-0">
                  <section className="min-h-0 h-full">
                    <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                      <CardHeader
                        title="User Access"
                        rightSlot={<Pill>{usersLoading ? "Loading..." : `${managedUsers.length}`}</Pill>}
                        icon={<IconShield className="w-6 h-6" />}
                      />

                      <div className="px-5 pt-5">
                        <input
                          value={managementSearch}
                          onChange={(e) => setManagementSearch(e.target.value)}
                          placeholder="Search users by name or email"
                          className="w-full rounded-2xl border border-[#1F380024] bg-white/90 px-4 py-3 text-sm outline-none"
                        />
                      </div>

                      <div className="flex-1 min-h-0 p-5">
                        <SoftPanel className="min-h-0 h-full">
                          <div className="h-full overflow-y-auto pr-1 space-y-2" style={{ scrollbarGutter: "stable" }}>
                            {usersLoading ? (
                              <EmptyBox label="Loading users..." />
                            ) : managedUsers.length === 0 ? (
                              <EmptyBox label="No matching users." />
                            ) : (
                              managedUsers.map((u) => (
                                <ListRow
                                  key={u.uid}
                                  active={u.uid === selectedManagedUserId}
                                  onClick={() => setSelectedManagedUserId(u.uid)}
                                  title={getUserDisplayName(u)}
                                  subtitle={u.email || "—"}
                                  meta=""
                                  right={
                                    <div className="flex items-center gap-1.5">
                                      {u.management ? <Pill>M</Pill> : null}
                                      {u.staff ? <Pill>S</Pill> : null}
                                    </div>
                                  }
                                />
                              ))
                            )}
                          </div>
                        </SoftPanel>
                      </div>
                    </CardShell>
                  </section>

                  <section className="min-h-0 h-full">
                    <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                      <CardHeader
                        title="Permissions"
                        rightSlot={selectedManagedUser ? <PresencePill status={getEffectiveStaffStatus(selectedManagedUser)} /> : null}
                        icon={<IconShield className="w-6 h-6" />}
                      />

                      <div className="flex-1 min-h-0 p-5 overflow-y-auto">
                        <SectionCard>
                          {selectedManagedUser ? (
                            <>
                              <div className="text-lg font-extrabold" style={{ color: THEME.text }}>
                                {getUserDisplayName(selectedManagedUser)}
                              </div>

                              <div className="text-sm text-[rgba(31,56,0,0.62)] mt-1">
                                {selectedManagedUser.email || "—"}
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {selectedManagedUser.management ? <Pill>Management</Pill> : null}
                                {selectedManagedUser.staff ? <Pill>Staff</Pill> : null}
                                {selectedManagedUser.approved ? <Pill>Approved</Pill> : null}
                              </div>

                              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                                  <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                    Staff Access
                                  </div>
                                  <div className="mt-3 text-sm text-[rgba(31,56,0,0.65)]">
                                    Gives access to staff tools and staff status features.
                                  </div>
                                  <div className="mt-4">
                                    <GreenButton
                                      className="w-full"
                                      onClick={() => toggleUserRole(selectedManagedUser.uid, "staff", !!selectedManagedUser.staff)}
                                    >
                                      {selectedManagedUser.staff ? "Remove Staff" : "Make Staff"}
                                    </GreenButton>
                                  </div>
                                </div>

                                <div className="rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                                  <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                    Management Access
                                  </div>
                                  <div className="mt-3 text-sm text-[rgba(31,56,0,0.65)]">
                                    Reserved for users who can manage staff permissions and inquiry types.
                                  </div>
                                  <div className="mt-4">
                                    <GreenButton
                                      className="w-full"
                                      onClick={() => toggleUserRole(selectedManagedUser.uid, "management", !!selectedManagedUser.management)}
                                      disabled={selectedManagedUser.uid === currentUid}
                                    >
                                      {selectedManagedUser.management ? "Remove Management" : "Make Management"}
                                    </GreenButton>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50/80 p-4">
                                <div className="text-xs font-extrabold uppercase tracking-wide text-red-700">
                                  Delete User Account
                                </div>

                                <div className="mt-3 text-sm text-red-800/80 leading-relaxed">
                                  This removes the user&apos;s Firebase Auth login and deletes their user profile document.
                                  Old inquiries, messages, and support records are not deleted.
                                </div>

                                <div className="mt-4">
                                  <DangerButton
                                    className="w-full"
                                    onClick={deleteManagedUser}
                                    disabled={deletingUser || selectedManagedUser.uid === currentUid}
                                  >
                                    {deletingUser ? "Deleting..." : "Delete User Account"}
                                  </DangerButton>
                                </div>

                                {selectedManagedUser.uid === currentUid ? (
                                  <div className="mt-3 text-xs font-semibold text-red-700/80">
                                    You cannot delete your own account from this panel.
                                  </div>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-[rgba(31,56,0,0.62)]">Select a user to manage access.</div>
                          )}
                        </SectionCard>
                      </div>
                    </CardShell>
                  </section>
                </div>
              ) : null}

              {managementSubTab === "types" ? (
                <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6 h-full min-h-0">
                  <section className="min-h-0 h-full">
                    <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                      <CardHeader
                        title="Inquiry Types"
                        rightSlot={<Pill>{typesLoading ? "Loading..." : `${filteredInquiryTypes.length}`}</Pill>}
                        icon={<IconList className="w-6 h-6" />}
                      />

                      <div className="px-5 pt-5 flex items-center gap-3">
                        <input
                          value={typeSearch}
                          onChange={(e) => setTypeSearch(e.target.value)}
                          placeholder="Search inquiry types"
                          className="w-full rounded-2xl border border-[#1F380024] bg-white/90 px-4 py-3 text-sm outline-none"
                        />
                        <SecondaryButton onClick={createNewBlankType}>New</SecondaryButton>
                      </div>

                      <div className="flex-1 min-h-0 p-5">
                        <SoftPanel className="min-h-0 h-full">
                          <div className="h-full overflow-y-auto pr-1 space-y-2" style={{ scrollbarGutter: "stable" }}>
                            {typesLoading ? (
                              <EmptyBox label="Loading inquiry types..." />
                            ) : filteredInquiryTypes.length === 0 ? (
                              <EmptyBox label="No inquiry types found." />
                            ) : (
                              filteredInquiryTypes.map((item) => (
                                <ListRow
                                  key={item.id}
                                  active={!isCreatingNewType && item.id === selectedInquiryTypeId}
                                  onClick={() => openExistingInquiryType(item.id)}
                                  title={item.label || item.key || item.id}
                                  subtitle={`${item.active ? "Active" : "Inactive"} • ${item.autoOpenThread ? "Auto thread on" : "Auto thread off"}`}
                                  meta={`#${item.sortOrder}`}
                                  right={<Pill>{item.key}</Pill>}
                                />
                              ))
                            )}
                          </div>
                        </SoftPanel>
                      </div>
                    </CardShell>
                  </section>

                  <section className="min-h-0 h-full">
                    <CardShell className="h-full min-h-0 overflow-hidden flex flex-col">
                      <CardHeader
                        title="Inquiry Type Editor"
                        rightSlot={
                          <div className="flex items-center gap-2">
                            {isCreatingNewType ? <Pill>Creating New</Pill> : typeEditor.id ? <Pill>{typeEditor.id}</Pill> : <Pill>New</Pill>}

                            {!isCreatingNewType && typeEditor.id ? (
                              <SecondaryButton onClick={archiveInquiryType}>
                                Archive
                              </SecondaryButton>
                            ) : null}

                            <GreenButton onClick={saveInquiryType} disabled={savingType}>
                              {savingType ? "Saving..." : "Save Type"}
                            </GreenButton>
                          </div>
                        }
                        icon={<IconList className="w-6 h-6" />}
                      />

                      <div className="flex-1 min-h-0 p-5 overflow-y-auto">
                        <SectionCard>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                                Label
                              </label>
                              <input
                                value={typeEditor.label}
                                onChange={(e) => updateTypeEditorField("label", e.target.value)}
                                className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                                placeholder="Housing Stability & Urgency"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                                Key
                              </label>
                              <input
                                value={typeEditor.key}
                                onChange={(e) => updateTypeEditorField("key", slugifyInquiryTypeKey(e.target.value))}
                                className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                                placeholder="housing_stability_urgency"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                                Sort Order
                              </label>
                              <input
                                type="number"
                                value={typeEditor.sortOrder}
                                onChange={(e) => updateTypeEditorField("sortOrder", Number(e.target.value || 0))}
                                className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                              />
                            </div>

                            <div className="flex items-end gap-5">
                              <label className="flex items-center gap-2 text-sm font-extrabold text-[rgba(31,56,0,0.70)]">
                                <input
                                  type="checkbox"
                                  checked={typeEditor.active}
                                  onChange={(e) => updateTypeEditorField("active", e.target.checked)}
                                />
                                Active
                              </label>

                              <label className="flex items-center gap-2 text-sm font-extrabold text-[rgba(31,56,0,0.70)]">
                                <input
                                  type="checkbox"
                                  checked={typeEditor.autoOpenThread}
                                  onChange={(e) => updateTypeEditorField("autoOpenThread", e.target.checked)}
                                />
                                Auto-open thread
                              </label>
                            </div>
                          </div>

                          <div className="mt-5">
                            <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                              Description
                            </label>
                            <textarea
                              rows={3}
                              value={typeEditor.description}
                              onChange={(e) => updateTypeEditorField("description", e.target.value)}
                              className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                              placeholder="Describe when this inquiry type should be used."
                            />
                          </div>

                          <div className="mt-5">
                            <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                              Default Thread Message
                            </label>
                            <textarea
                              rows={7}
                              value={typeEditor.defaultThreadMessage}
                              onChange={(e) => updateTypeEditorField("defaultThreadMessage", e.target.value)}
                              className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                              placeholder="This message is automatically posted when a new inquiry of this type is submitted."
                            />
                          </div>

                          <div className="mt-5">
                            <label className="block text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)] mb-2">
                              Follow-up Questions
                            </label>
                            <textarea
                              rows={10}
                              value={(typeEditor.followUpQuestions || []).join("\n")}
                              onChange={(e) => updateTypeEditorQuestionsFromText(e.target.value)}
                              className="w-full rounded-2xl border border-[#1F380024] bg-white/95 px-4 py-3 text-sm outline-none"
                              placeholder="One question per line"
                            />
                          </div>

                          {typeEditor.followUpQuestions?.length ? (
                            <div className="mt-5 rounded-2xl border border-[#1F380024] bg-white/80 p-4">
                              <div className="text-xs font-extrabold uppercase tracking-wide text-[rgba(31,56,0,0.45)]">
                                Preview
                              </div>
                              <div className="mt-3 text-sm text-[rgba(31,56,0,0.70)]">
                                <div className="font-extrabold" style={{ color: THEME.text }}>
                                  {typeEditor.label || "Untitled Inquiry Type"}
                                </div>

                                {typeEditor.description ? (
                                  <div className="mt-2">{typeEditor.description}</div>
                                ) : null}

                                <ul className="mt-3 space-y-2">
                                  {typeEditor.followUpQuestions.map((q, idx) => (
                                    <li key={`preview_q_${idx}`}>• {q}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          ) : null}
                        </SectionCard>
                      </div>
                    </CardShell>
                  </section>
                </div>
              ) : null}

              {managementSubTab === "theme" ? (
                <div className="h-full min-h-0">
                  <ColorEditor />
                </div>
              ) : null}

              {managementSubTab === "maintenance" ? (
                <div className="h-full min-h-0 overflow-y-auto">
                  <SectionCard>
                    <div className="space-y-4">
                      <div>
                        <h3
                          className="text-lg font-extrabold"
                          style={{ color: THEME.text }}
                        >
                          Cleanup orphan threads
                        </h3>
                        <p
                          className="text-sm mt-1"
                          style={{ color: "rgba(31,56,0,0.66)" }}
                        >
                          Deletes thread documents that have no messages. These
                          can be left behind by a failed inquiry submission.
                          Threads created in the last 5 minutes are skipped.
                        </p>
                      </div>

                      <button
                        onClick={handleCleanupOrphanThreads}
                        disabled={cleaningUpThreads}
                        className="py-2 px-4 rounded-2xl text-white font-extrabold disabled:cursor-not-allowed transition hover:opacity-90"
                        style={{
                          backgroundColor: cleaningUpThreads
                            ? "rgba(31,56,0,0.28)"
                            : THEME.primary,
                        }}
                        type="button"
                      >
                        {cleaningUpThreads ? "Cleaning up..." : "Run cleanup"}
                      </button>

                      {lastCleanupResult ? (
                        <div
                          className="rounded-2xl border p-3 text-sm"
                          style={{
                            backgroundColor: THEME.cardBgSoft,
                            borderColor: THEME.border,
                            color: "rgba(31,56,0,0.7)",
                          }}
                        >
                          Scanned {lastCleanupResult.scanned} thread
                          {lastCleanupResult.scanned === 1 ? "" : "s"},
                          deleted {lastCleanupResult.deleted} orphan
                          {lastCleanupResult.deleted === 1 ? "" : "s"}.
                        </div>
                      ) : null}
                    </div>
                  </SectionCard>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
